import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import jm, { errorMessage } from '../api';

/**
 * The list-plus-dialog behaviour every stage tab shares.
 *
 * `fetchList` returns the tab's rows; `saveStage(id, payload)` writes the
 * dialog back. Opening a row pulls that candidate's full cross-stage record so
 * the dialog can show what earlier rounds concluded.
 */
const useStage = (fetchList, saveStage, { onMoved } = {}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);   // the list row being edited
  const [detail, setDetail] = useState(null);     // its full cross-stage record
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return fetchList()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((error) => toast.error(errorMessage(error, 'Could not load the list')))
      .finally(() => setLoading(false));
  }, [fetchList]);

  useEffect(() => { reload(); }, [reload]);

  const openEdit = useCallback((row) => {
    setEditing(row);
    setDetail(null);
    setDetailLoading(true);

    jm.candidate(row.application_id)
      .then(setDetail)
      .catch((error) => toast.error(errorMessage(error, 'Could not load the candidate')))
      .finally(() => setDetailLoading(false));
  }, []);

  const closeEdit = useCallback(() => {
    setEditing(null);
    setDetail(null);
  }, []);

  const save = useCallback(
    async (payload) => {
      if (saving || !editing) return;
      setSaving(true);

      try {
        const result = await saveStage(editing.application_id, payload);
        toast.success(result.message || 'Candidate updated');

        closeEdit();
        await reload();

        // A candidate who left this stage should surface in the tab they moved to.
        if (onMoved && result.moved_to) onMoved(result.moved_to, result);
      } catch (error) {
        toast.error(errorMessage(error));
      } finally {
        setSaving(false);
      }
    },
    [saving, editing, saveStage, closeEdit, reload, onMoved]
  );

  return { rows, loading, reload, editing, detail, detailLoading, openEdit, closeEdit, saving, save };
};

export default useStage;
